import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as CryptoJS from 'crypto-js';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, map, Observable, retry, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { environment } from '../../environments/environment';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly MENU_KEY = 'activeMenu';
  private activeMenuSubject!: BehaviorSubject<string>;
  activeMenu$!: Observable<string>;
  private readonly secretKey = 'my-secret-key-123'; // 🔑 keep safe

  constructor(
    private http: HttpClient,
    private router: Router,
    private cookieService: CookieService
  ) {
    const initialMenu = this.getInitialMenu();
    this.activeMenuSubject = new BehaviorSubject<string>(initialMenu);
    this.activeMenu$ = this.activeMenuSubject.asObservable();
  }

  private getInitialMenu(): string {
    return this.cookieService.get(this.MENU_KEY) || 'dashboard';
  }

  setActiveMenu(menu: string): void {
    this.activeMenuSubject.next(menu);
    this.cookieService.set(this.MENU_KEY, menu);
  }
  profileService() {
    throw new Error('Method not implemented.');
  }
  public apiUrl = environment.API_URL;

  getAccessToken(): string | null {
    return this.cookieService.get('accessToken');
  }

  // Get the refresh token from localStorage
  getRefreshToken(): string | null {
    return this.cookieService.get('refreshToken');
  }

  // Store the new access token in localStorage
  setAccessToken(token: string): void {
    this.cookieService.set('accessToken', token);
  }

  getUserId(): string | null {
    const encrypted = this.cookieService.get('UserId');
    if (!encrypted) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, this.secretKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  }
  getUserTypeId(): string | null {
    return this.cookieService.get('userTypeId');
  }

  // Store the new access token in localStorage
  setUserId(id: string): void {
    const encrypted = CryptoJS.AES.encrypt(id, this.secretKey).toString();
    this.cookieService.set('UserId', encrypted);
  }
  // Encrypt and store user type
  setUserType(userType: string): void {
    const encrypted = CryptoJS.AES.encrypt(userType, this.secretKey).toString();
    this.cookieService.set('userType', encrypted);
  }
  setStaffId(staffId: string): void {
    const encrypted = CryptoJS.AES.encrypt(staffId, this.secretKey).toString();
    this.cookieService.set('staffId', encrypted);
  }

  // Decrypt and read user type
  getUserType(): string | null {
    const encrypted = this.cookieService.get('userType');
    if (!encrypted) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, this.secretKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  }
  getStaffId(): string | null {
    const encrypted = this.cookieService.get('staffId');
    if (!encrypted) return null;

    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, this.secretKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return null;
    }
  }

  // Clear cookie
  clearUserType(): void {
    this.cookieService.delete('userType');
  }

  setUserTypeId(id: string): void {
    this.cookieService.set('userTypeId', id);
  }
  // Store the refresh token in localStorage
  setRefreshToken(token: string): void {
    this.cookieService.set('refreshToken', token);
  }

  isProfileVerified(): boolean {
    const status = this.getProfileStatus();
    return ['approved', 'under_verification', 'resubmitted'].includes(status);
  }
  getProfileStatus(): string {
    return localStorage.getItem('verified') ?? '';
  }
  // Attempt to refresh the access token using the refresh token
  refreshToken(): Observable<any> {
    console.log('this.getRefreshToken()', this.getAccessToken());
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available.'));
    }
    const payload = {
      refreshToken: refreshToken,
    };
    return this.http.post(`${this.apiUrl}${environment.REFRESH_TOKEN}`, payload).pipe(
      map((response: any) => {
        this.setAccessToken(response.data.accessToken);
        this.setRefreshToken(response.data.refreshToken);
        return response;
      }),
      catchError((error) => {
        return throwError(() => new Error('Token refresh failed.'));
      })
    );
  }

  login(data: any): Observable<any> {
    return this.http
      .post(`${this.apiUrl}${environment.LOGIN}`, data)
      .pipe(retry(1), catchError(this.errorHandler));
  }
  forgotPassword(data: any): Observable<any> {
    return this.http
      .post(`${this.apiUrl}${environment.FORGOT_PASSWORD}`, data)
      .pipe(retry(1), catchError(this.errorHandler));
  }
  otpVerifications(data: any): Observable<any> {
    return this.http
      .post(`${this.apiUrl}${environment.OTP_VARIFICATION}`, data)
      .pipe(retry(1), catchError(this.errorHandler));
  }
  resetPassword(data: any): Observable<any> {
    return this.http
      .post(`${this.apiUrl}${environment.RESET_PASSWORD}`, data)
      .pipe(retry(1), catchError(this.errorHandler));
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    return !!(accessToken && refreshToken);
  }

  // Clear all user session data thoroughly
  clearSession(): void {
    // Clear specific cookies with different path combinations
    const cookiesToClear = ['accessToken', 'refreshToken', this.MENU_KEY];
    const paths = ['/', '', window.location.pathname];

    cookiesToClear.forEach((cookieName) => {
      paths.forEach((path) => {
        try {
          this.cookieService.delete(cookieName, path);
          this.cookieService.delete(cookieName, path, window.location.hostname);
          this.cookieService.delete(cookieName, path, '.' + window.location.hostname);
        } catch (error) {
          console.warn(`Failed to delete cookie ${cookieName} with path ${path}:`, error);
        }
      });
    });

    // Also try the deleteAll method as fallback
    try {
      this.cookieService.deleteAll();
    } catch (error) {
      console.warn('Failed to delete all cookies:', error);
    }

    // Reset active menu
    this.activeMenuSubject.next('dashboard');
  }
  logout(): void {
    try {
      // Clear all session data thoroughly
      this.clearSession();

      // Navigate to login page
      this.router.navigate(['/login']);

      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error during logout:', error);
      // Still try to clear session and navigate even if there's an error
      try {
        this.clearSession();
      } catch (clearError) {
        console.error('Failed to clear session:', clearError);
      }
      this.router.navigate(['/login']);
    }
  }

  // Force logout - useful when tokens are invalid or expired
  forceLogout(reason?: string): void {
    console.warn('Force logout triggered:', reason || 'Unknown reason');

    // Clear session without any error handling
    this.clearSession();

    // Navigate to login immediately
    this.router.navigate(['/login']);

    // // Show user-friendly message
    // if (reason) {
    //   this.showError(`Session expired: ${reason}`);
    // }
  }

  confirmDelete(
    title: string = 'Are you sure?',
    text: string = "You won't be able to revert this!"
  ): Promise<boolean> {
    return Swal.fire({
      title,
      text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel',
    }).then((result) => result.isConfirmed);
  }

  showSuccess(message: string) {
    Swal.fire('Success!', message, 'success');
  }

  showError(message: string) {
    Swal.fire('Error!', message, 'error');
  }
  private errorHandler(error: any): Observable<never> {
    return throwError(error);
  }
}
